/**
 * UIManager - DOM interface abstraction for Genesys scenes. Wraps HTML elements into UIElement/UILayout
 * helpers, connects with the input system via BaseInputHandler, and manages UI lifecycle.
 */

import { BaseInputHandler } from '../systems/InputManager.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';


import { UIElement } from './UIElement.js';
import { UILayout } from './UILayout.js';

import type { UIElementOptions } from './UIElement.js';
import type { UILayoutOptions } from './UILayout.js';
import type { UIResource } from './UIResource.js';
import type { MouseButton } from '../systems/InputManager.js';

export interface UIManagerOptions {
  container?: HTMLElement;
}

export class UIManager extends BaseInputHandler {
  private container?: HTMLElement;
  private uiContainer?: HTMLElement;
  private layouts: Map<string, UILayout> = new Map();
  private elements: Map<string, UIElement> = new Map();

  constructor(options: UIManagerOptions) {
    super();
    ObjectTracker.track(this);
    this.container = options.container;
    this.createUIContainer();
  }

  private createUIContainer(): void {
    if (!this.container) {
      return;
    }

    this.uiContainer = document.createElement('div');
    this.uiContainer.id = 'ui-container';
    this.uiContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
    `;
    this.container.appendChild(this.uiContainer);
  }

  public createElement(id: string, options: UIElementOptions = {}): UIElement {
    if (this.elements.has(id)) {
      throw new Error(`UI element with id '${id}' already exists`);
    }

    const element = new UIElement({
      ...options,
      id: id
    });

    if (this.uiContainer) {
      element.appendTo(this.uiContainer);
    }

    // Enable pointer events for this element
    element.setStyle('pointerEvents', 'auto');

    this.elements.set(id, element);
    element.registerWithUIManager(this);
    return element;
  }

  public createLayout(id: string, options: UILayoutOptions): UILayout {
    if (this.layouts.has(id)) {
      throw new Error(`UI layout with id '${id}' already exists`);
    }

    const layout = new UILayout(options);
    if (this.uiContainer) {
      layout.appendTo(this.uiContainer);
    }

    // Enable pointer events for the layout
    layout.getElement().setStyle('pointerEvents', 'auto');

    this.layouts.set(id, layout);
    layout.registerWithUIManager(this);
    return layout;
  }

  public getElement(id: string): UIElement | undefined {
    return this.elements.get(id);
  }

  public getLayout(id: string): UILayout | undefined {
    return this.layouts.get(id);
  }

  public clear(): void {
    // Remove all layouts
    this.layouts.forEach(layout => layout.remove());
    this.layouts.clear();

    // Remove all elements
    this.elements.forEach(element => element.remove());
    this.elements.clear();
  }

  public destroy(): void {
    this.clear();
    this.uiContainer?.remove();

    // Remove this UIManager from the input manager
    if (this.inputManager) {
      this.inputManager.removeInputHandler(this);
    }
  }

  public unregisterUIResource(resource: UIResource): void {
    for (const [id, element] of this.elements) {
      if (element === resource) {
        this.elements.delete(id);
        break;
      }
    }
    for (const [id, layout] of this.layouts) {
      if (layout === resource) {
        this.layouts.delete(id);
        break;
      }
    }
  }

  // Input handling - UI elements should consume input when interacted with
  public override handleMouseDown(button: MouseButton, e: MouseEvent): boolean {
    // Check if the click was on a UI element
    const target = e.target as HTMLElement;
    return this.isUIElement(target);
  }

  public override handleMouseUp(button: MouseButton, e: MouseEvent): boolean {
    const target = e.target as HTMLElement;
    return this.isUIElement(target);
  }

  public override handleMouseMove(e: MouseEvent): boolean {
    const target = e.target as HTMLElement;
    return this.isUIElement(target);
  }

  public override handleMouseClick(button: MouseButton, e: MouseEvent): boolean {
    const target = e.target as HTMLElement;
    return this.isUIElement(target);
  }

  private isUIElement(element: HTMLElement): boolean {
    // Check if the element is within our UI container
    return this.uiContainer?.contains(element) ?? false;
  }
}
